import React, { useState } from 'react';

const emptyForm = {
  customerName: '',
  customerPhone: '',
  customerAddress: '',
  notes: '',
  paymentMethod: 'cod',
};

export { emptyForm };

const PHONE_MIN_DIGITS = 9;
function isValidPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= PHONE_MIN_DIGITS;
}

export default function CheckoutForm({
  form,
  onFormChange,
  onPlaceOrder,
  isPlacingOrder,
  cartEmpty,
  error,
  success,
  onLookupCustomer,
}) {
  const [lookupMessage, setLookupMessage] = useState('');
  const phoneValid = !form.customerPhone || isValidPhone(form.customerPhone);
  const showPhoneError = form.customerPhone && !phoneValid;

  const handleLookup = async () => {
    const phone = (form.customerPhone || '').trim();
    if (!phone || !onLookupCustomer) return;
    setLookupMessage('');
    try {
      const customer = await onLookupCustomer(phone);
      if (customer) {
        onFormChange((c) => ({
          ...c,
          customerName: customer.name || c.customerName,
          customerPhone: customer.phone || c.customerPhone,
          customerAddress: customer.address || c.customerAddress,
        }));
        setLookupMessage('Customer found. Details filled.');
      } else {
        setLookupMessage('No customer found for this phone.');
      }
    } catch (_) {
      setLookupMessage('Lookup failed.');
    }
  };

  return (
    <div className="mt-6 border-t pt-4 space-y-2">
      <h4 className="font-semibold">Checkout</h4>
      <input
        className="border w-full p-3 sm:p-2 text-base sm:text-sm min-h-[44px] sm:min-h-0"
        placeholder="Full Name"
        value={form.customerName}
        onChange={(e) => onFormChange((c) => ({ ...c, customerName: e.target.value }))}
        aria-label="Full name"
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          className={`min-h-[44px] border w-full p-3 text-base sm:p-2 sm:text-sm ${showPhoneError ? 'border-red-500' : ''}`}
          placeholder="Phone Number (at least 9 digits)"
          value={form.customerPhone}
          onChange={(e) => onFormChange((c) => ({ ...c, customerPhone: e.target.value }))}
          aria-label="Phone number"
          aria-invalid={showPhoneError}
          aria-describedby={showPhoneError ? 'phone-error' : undefined}
        />
        {onLookupCustomer && (
          <button type="button" onClick={handleLookup} className="min-h-[44px] border rounded px-3 py-2 text-sm whitespace-nowrap" aria-label="Look up customer by phone">
            Look up
          </button>
        )}
      </div>
      {showPhoneError && <p id="phone-error" className="text-xs text-red-600">Enter a valid phone number (at least 9 digits).</p>}
      {lookupMessage && !showPhoneError && <p className="text-xs text-gray-600">{lookupMessage}</p>}
      <textarea
        className="min-h-[84px] border w-full p-3 text-base sm:p-2 sm:text-sm"
        placeholder="Delivery Address"
        value={form.customerAddress}
        onChange={(e) => onFormChange((c) => ({ ...c, customerAddress: e.target.value }))}
        aria-label="Delivery address"
      />
      <textarea
        className="min-h-[72px] border w-full p-3 text-base sm:p-2 sm:text-sm"
        placeholder="Notes (optional)"
        value={form.notes}
        onChange={(e) => onFormChange((c) => ({ ...c, notes: e.target.value }))}
        aria-label="Order notes"
      />
      <select
        className="min-h-[44px] border w-full p-3 text-base sm:p-2 sm:text-sm"
        value={form.paymentMethod}
        onChange={(e) => onFormChange((c) => ({ ...c, paymentMethod: e.target.value }))}
        aria-label="Payment method"
      >
        <option value="cod">Cash on Delivery</option>
        <option value="online">Online Payment (Stub)</option>
      </select>
      <button
        type="button"
        onClick={onPlaceOrder}
        disabled={isPlacingOrder || cartEmpty || !phoneValid}
        className="min-h-[44px] w-full bg-green-600 text-white py-2 rounded text-sm font-medium disabled:opacity-60"
        aria-label="Place order"
      >
        {isPlacingOrder ? 'Placing Order...' : 'Place Order'}
      </button>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {success ? <p className="text-sm text-green-700">{success}</p> : null}
    </div>
  );
}
